import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Controls candidate rotation, local variant selection, and mobile carousel state.
 * Cart mutations remain owned by Horizon's native product-form-component.
 */
class CompleteLookSection extends Component {
  #abortController;
  #scrollFrame;
  #imageTokens = new WeakMap();
  #moneyFormatters = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onOptionChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    this.addEventListener(StandardEvents.cartError, this.#onCartError, { signal });

    const track = this.querySelector('[data-ctc-track]');
    track?.addEventListener('scroll', this.#scheduleDotUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleDotUpdate, { passive: true, signal });

    this.#mountCandidates();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    super.disconnectedCallback();
  }

  #mountCandidates() {
    const track = this.querySelector('[data-ctc-track]');
    const pool = this.querySelector('[data-ctc-pool]');
    if (!(track instanceof HTMLElement) || !(pool instanceof HTMLElement)) return;

    let cards = Array.from(track.querySelectorAll('[data-candidate-card]'));

    if (cards.length === 0) {
      const candidates = Array.from(pool.querySelectorAll('[data-candidate-template]'));
      const count = Math.min(Number(this.dataset.productsLimit) || 4, candidates.length);
      const selectedTemplates = this.#chooseCandidates(candidates, count);
      cards = selectedTemplates
        .map((template) => (template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null))
        .filter((card) => card instanceof HTMLElement);

      for (const card of cards) {
        card.hidden = false;
        track.append(card);
      }
    }

    if (cards.length === 0) {
      if (this.dataset.editorMode !== 'true') this.hidden = true;
      return;
    }

    for (const card of cards) this.#initializeCard(card);
    this.#buildDots(cards);
    this.#updateActiveDot();
  }

  /** @param {Element[]} candidates @param {number} count */
  #chooseCandidates(candidates, count) {
    const key = `complete-look:${this.dataset.sectionId}:${this.dataset.currentProductId}`;
    let recentIds = [];

    try {
      recentIds = JSON.parse(sessionStorage.getItem(key) || '[]');
      if (!Array.isArray(recentIds)) recentIds = [];
    } catch {
      recentIds = [];
    }

    const recentSet = new Set(recentIds.map(String));
    const fresh = candidates.filter((card) => !recentSet.has(String(card.dataset.productId)));
    const source = fresh.length >= count ? fresh : candidates;
    let selected = this.#shuffle(source).slice(0, count);

    if (source === candidates && candidates.length > count && this.#isSameSelection(selected, recentSet, count)) {
      const replacement = this.#shuffle(candidates.filter((card) => !recentSet.has(String(card.dataset.productId))))[0];
      if (replacement) selected[selected.length - 1] = replacement;
    }

    const selectedIds = selected.map((card) => String(card.dataset.productId));
    try {
      sessionStorage.setItem(key, JSON.stringify(selectedIds));
    } catch {
      // Storage can be unavailable in privacy modes; rotation still works for this load.
    }

    return selected;
  }

  /** @param {Element[]} selected @param {Set<string>} recentSet @param {number} count */
  #isSameSelection(selected, recentSet, count) {
    return recentSet.size === count && selected.every((card) => recentSet.has(String(card.dataset.productId)));
  }

  /** @template T @param {T[]} values @returns {T[]} */
  #shuffle(values) {
    const shuffled = values.slice();
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }
    return shuffled;
  }

  /** @param {Element} card */
  #initializeCard(card) {
    if (!(card instanceof HTMLElement)) return;

    const dataElement = card.querySelector('[data-variant-data]');
    if (!(dataElement instanceof HTMLScriptElement)) return;

    try {
      const parsed = JSON.parse(dataElement.textContent || '{}');
      card.variantData = Array.isArray(parsed.variants) ? parsed.variants : [];
      card.currencyCode = parsed.currency || 'USD';
    } catch (error) {
      console.warn('[Completa tu conjunto] No se pudieron leer las variantes.', error);
      card.variantData = [];
      card.currencyCode = 'USD';
    }

    this.#resolveCardVariant(card);
  }

  #onOptionChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-option-index]')) return;
    const card = event.target.closest('[data-candidate-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;

    this.#hideError(card);
    this.#resolveCardVariant(card);
  };

  /** @param {HTMLElement} card */
  #resolveCardVariant(card) {
    const variants = Array.isArray(card.variantData) ? card.variantData : [];
    const selects = Array.from(card.querySelectorAll('[data-option-index]')).sort(
      (first, second) => Number(first.dataset.optionIndex) - Number(second.dataset.optionIndex)
    );

    this.#updateOptionAvailability(selects, variants);
    const selectedValues = selects.map((select) => select.value);
    const variant = variants.find(
      (item) => item.options.length === selectedValues.length && item.options.every((value, index) => value === selectedValues[index])
    );

    card.selectedVariant = variant || null;
    this.#updateCard(card, variant || null);
  }

  /** @param {HTMLSelectElement[]} selects @param {Array<any>} variants */
  #updateOptionAvailability(selects, variants) {
    const selectedValues = selects.map((select) => select.value);

    for (let index = 0; index < selects.length; index += 1) {
      const select = selects[index];
      const availability = Array.from(select.options).map((option) =>
        variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === option.value &&
            variant.options.slice(0, index).every((value, previousIndex) => value === selectedValues[previousIndex])
        )
      );
      const hasAvailableValue = availability.some(Boolean);

      Array.from(select.options).forEach((option, optionIndex) => {
        option.disabled = hasAvailableValue && !availability[optionIndex];
      });

      if (select.selectedOptions[0]?.disabled) {
        const firstAvailable = Array.from(select.options).find((option) => !option.disabled);
        if (firstAvailable) select.value = firstAvailable.value;
      }

      selectedValues[index] = select.value;
    }
  }

  /** @param {HTMLElement} card @param {any | null} variant */
  #updateCard(card, variant) {
    const input = card.querySelector('[data-variant-id-input]');
    if (input instanceof HTMLInputElement) input.value = variant?.id ? String(variant.id) : '';

    const available = Boolean(variant?.available);
    const label = !variant
      ? this.dataset.unavailableText
      : available
        ? this.dataset.addText
        : this.dataset.soldOutText;

    for (const button of card.querySelectorAll('[data-ctc-add-button]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    }

    const labelElement = card.querySelector('[data-add-label]');
    if (labelElement) labelElement.textContent = label || '';

    const price = card.querySelector('[data-price]');
    const compare = card.querySelector('[data-compare-price]');
    const badge = card.querySelector('[data-sale-badge]');
    const hasSale = Boolean(variant && Number(variant.compareAtPrice) > Number(variant.price));

    if (price && variant) {
      price.textContent = this.#formatMoney(Number(variant.price), card.currencyCode);
      price.classList.toggle('ctc-card__price--sale', hasSale);
    }
    if (compare) {
      compare.hidden = !hasSale;
      compare.textContent = hasSale ? this.#formatMoney(Number(variant.compareAtPrice), card.currencyCode) : '';
    }
    if (badge) badge.hidden = !hasSale;

    const imageUrl = variant?.image || card.dataset.defaultImage || '';
    const thumbnail = variant?.thumbnail || imageUrl;
    const imageAlt = variant?.imageAlt || card.dataset.defaultImageAlt || '';
    this.#updateImage(card, imageUrl, imageAlt);

    for (const component of card.querySelectorAll('add-to-cart-component')) {
      if (component instanceof HTMLElement) component.dataset.productVariantMedia = thumbnail;
    }
  }

  /** @param {HTMLElement} card @param {string} url @param {string} alt */
  #updateImage(card, url, alt) {
    const image = card.querySelector('[data-candidate-card] img, .ctc-card__image');
    if (!(image instanceof HTMLImageElement) || !url || image.currentSrc === url || image.src === url) return;

    const token = (this.#imageTokens.get(card) || 0) + 1;
    this.#imageTokens.set(card, token);
    image.classList.add('is-updating');

    const preload = new Image();
    preload.onload = () => {
      if (this.#imageTokens.get(card) !== token) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
      image.alt = alt;
      requestAnimationFrame(() => image.classList.remove('is-updating'));
    };
    preload.onerror = () => {
      if (this.#imageTokens.get(card) === token) image.classList.remove('is-updating');
    };
    preload.src = url;
  }

  /** @param {number} cents @param {string} currency */
  #formatMoney(cents, currency) {
    const locale = document.documentElement.lang || 'es-MX';
    const fractionDigits = cents % 100 === 0 ? 0 : 2;
    const key = `${locale}:${currency}:${fractionDigits}`;
    let formatter = this.#moneyFormatters.get(key);

    try {
      if (!formatter) {
        formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        });
        this.#moneyFormatters.set(key, formatter);
      }
      return formatter.format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(fractionDigits)} ${currency}`;
    }
  }

  #onCartLinesUpdate = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest('[data-candidate-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;

    this.#setLoading(card, true);
    this.#hideError(card);

    if (!event.promise) {
      this.#setLoading(card, false);
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (detail?.didError) this.#showError(card, this.dataset.errorText);
      })
      .catch(() => this.#showError(card, this.dataset.errorText))
      .finally(() => this.#setLoading(card, false));
  };

  #onCartError = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest('[data-candidate-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;
    this.#showError(card, event.error || this.dataset.errorText);
  };

  /** @param {HTMLElement} card @param {boolean} loading */
  #setLoading(card, loading) {
    card.classList.toggle('is-loading', loading);
    card.setAttribute('aria-busy', String(loading));

    for (const button of card.querySelectorAll('[data-ctc-add-button]')) {
      if (button instanceof HTMLButtonElement) button.disabled = loading || !card.selectedVariant?.available;
    }

    const label = card.querySelector('[data-add-label]');
    if (label) {
      label.textContent = loading
        ? this.dataset.addingText || ''
        : card.selectedVariant?.available
          ? this.dataset.addText || ''
          : card.selectedVariant
            ? this.dataset.soldOutText || ''
            : this.dataset.unavailableText || '';
    }
  }

  /** @param {HTMLElement} card @param {string} message */
  #showError(card, message) {
    const error = card.querySelector('[ref="addToCartTextError"]');
    if (!(error instanceof HTMLElement)) return;

    let textNode = Array.from(error.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim())
    );
    if (!textNode) {
      textNode = document.createTextNode('');
      error.append(textNode);
    }
    textNode.textContent = ` ${message || this.dataset.errorText || ''}`;
    error.classList.remove('hidden');
  }

  /** @param {HTMLElement} card */
  #hideError(card) {
    const error = card.querySelector('[ref="addToCartTextError"]');
    if (error instanceof HTMLElement) error.classList.add('hidden');
  }

  /** @param {HTMLElement[]} cards */
  #buildDots(cards) {
    const dots = this.querySelector('[data-ctc-dots]');
    if (!(dots instanceof HTMLElement)) return;
    dots.replaceChildren();

    cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ctc-dot';
      dot.dataset.dotIndex = String(index);
      dot.setAttribute('aria-label', `${this.dataset.goToProductText || 'Ir al producto'} ${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dots.append(dot);
    });
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('[data-dot-index]');
    if (!(dot instanceof HTMLButtonElement) || !this.contains(dot)) return;

    const track = this.querySelector('[data-ctc-track]');
    const cards = Array.from(this.querySelectorAll('[data-ctc-track] > [data-candidate-card]'));
    const card = cards[Number(dot.dataset.dotIndex)];
    if (!(track instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    track.scrollTo({
      left: card.offsetLeft - track.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  #scheduleDotUpdate = () => {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#updateActiveDot());
  };

  #updateActiveDot() {
    const track = this.querySelector('[data-ctc-track]');
    const cards = Array.from(this.querySelectorAll('[data-ctc-track] > [data-candidate-card]'));
    const dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    if (!(track instanceof HTMLElement) || cards.length === 0 || dots.length === 0) return;

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let activeIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      if (!(card instanceof HTMLElement)) return;
      const cardCenter = card.offsetLeft - track.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }
}

if (!customElements.get('complete-look-section')) {
  customElements.define('complete-look-section', CompleteLookSection);
}
